import { inject, Injectable } from '@angular/core';
import { MockService } from '@mock/mock.service';
import { IPosition } from '@app/core/interfaces/position.interface';
import { loggerDeploymentsDataMockData } from '@mock/data/logger-deployments-data-mock.data';
import { MarkersService } from '@mock/utils/markers.service';
import {
  getRandomBoolean,
  getRandomDateTime,
  getRandomInt,
  getRandomItem,
  getRandomPosition,
  getRandomStr,
} from '@app/shared/utils/random';
import { enumToArray } from '@app/shared/utils/enumerator';
import { LoggerType } from '@app/core/enums/logger-type.enum';
import { DeviceStatus } from '@app/core/enums/device-status.enum';
import {
  ILoggerDeploymentData,
  ILoggerDeploymentDataDetailed,
} from '@app/api/interfaces/logger-deployment-data.interface';
import { IComment, IUpdateCommentRequest } from '@app/api/interfaces/comment.interface';
import { delay, Observable } from 'rxjs';
import dayjs from 'dayjs';
import { IApiResponse } from '@app/api/interfaces/common/api-response.interface';
import { IPaginationMeta } from '@app/api/interfaces/common/pagination-meta.interface';
import { IListFilter } from '@app/api/interfaces/common/list-filter';
import { ProjectsMockService } from '@mock/data-services/projects-mock.service';
import { AreasMockService } from '@mock/data-services/areas-mock.service';
import { LoggerNbiotStatus } from '@app/core/enums/logger-nbiot-status.enum';
import { DeviceAntennaType } from '@app/core/enums/device-antenna-type.enum';
import { LoggerUpdateFirmwareState } from '@app/core/enums/logger-update-firmware-state.enum';
import { LoggerUpdateFirmwareType } from '@app/core/enums/logger-update-firmware-type.enum';
import { LoggerEcl } from '@app/core/enums/logger-ecl.enum';
import { CommonStateStatus } from '@app/core/enums/common-state-status.enum';
import { PositionStatus } from '@app/core/enums/position-status.enum';
import { CoordinateSystemsMockService } from '@mock/data-services/coordinate-systems-mock.service';

@Injectable({ providedIn: 'root' })
export class LoggerDeploymentsDataMockService extends MockService {
  private markersService = inject(MarkersService);
  private projectsMockService = inject(ProjectsMockService);
  private areasMockService = inject(AreasMockService);
  private coordinateSystemsMockService = inject(CoordinateSystemsMockService);

  public loggerDeploymentsData: ILoggerDeploymentData[] = [];

  private loggerDeploymentsDataPositions: IPosition[] = [];
  private id = 1;

  constructor() {
    super();

    if (this.isMockDataGenerated()) {
      this.loggerDeploymentsDataPositions = this.markersService.generateMarkersPositions(
        this.dataGeneratedCount.loggerDeploymentsData
      );
      this.loggerDeploymentsData = this.generateLoggerDeploymentsData();
    } else {
      this.loggerDeploymentsData = loggerDeploymentsDataMockData;
    }
  }

  public getAll(
    projectId: string,
    filter: IListFilter = {}
  ): Observable<IApiResponse<(ILoggerDeploymentData | ILoggerDeploymentDataDetailed)[], IPaginationMeta>> {
    const filteredLoggerDeploymentsData = this.isMockDataGenerated()
      ? this.loggerDeploymentsData
      : this.loggerDeploymentsData.filter(
          loggerDeploymentData =>
            loggerDeploymentData.projectId === projectId &&
            ((filter['areaId'] && filter['areaId'] === loggerDeploymentData.areaId) || !filter['areaId'])
        );

    const loggerDeploymentsData = filter['detailed']
      ? this.getLoggerDeploymentsDataDetailed(filteredLoggerDeploymentsData)
      : filteredLoggerDeploymentsData;

    return this.mockResponse(
      'LOGGER DEPLOYMENTS:getALL',
      {
        data: loggerDeploymentsData,
        meta: {
          pagination: {
            count: loggerDeploymentsData.length,
            total: loggerDeploymentsData.length,
            offset: 0,
            limit: loggerDeploymentsData.length,
          },
        },
      },
      arguments
    ).pipe(delay(this.requestDelay));
  }

  public getOne(deploymentId: string): Observable<IApiResponse<ILoggerDeploymentDataDetailed, object>> {
    const loggerDeploymentData = this.loggerDeploymentsData.find(item => item.deploymentId === deploymentId);

    const [loggerDeploymentDataDetailed] = this.getLoggerDeploymentsDataDetailed(
      loggerDeploymentData ? [loggerDeploymentData] : []
    );

    return this.mockResponse(
      'LOGGER DEPLOYMENTS:getOne',
      {
        data: loggerDeploymentDataDetailed as ILoggerDeploymentDataDetailed,
        meta: {},
      },
      arguments
    ).pipe(delay(this.requestDelay));
  }

  public updateComment(deploymentId: string, body: IUpdateCommentRequest): Observable<IApiResponse<IComment, object>> {
    const loggerDeploymentData = this.loggerDeploymentsData.find(item => item.deploymentId === deploymentId);

    const comment: IComment = {
      content: body.content,
      author: 'Julian Mercer',
      modifiedAt: dayjs().toISOString(),
    };

    if (loggerDeploymentData) {
      const index = this.loggerDeploymentsData.indexOf(loggerDeploymentData);
      this.loggerDeploymentsData.splice(index, 1, { ...loggerDeploymentData, comment });
    }

    return this.mockResponse(
      'LOGGER DEPLOYMENTS:updateComment',
      {
        data: comment,
        meta: {},
      },
      arguments
    ).pipe(delay(this.requestDelay));
  }

  private getLoggerDeploymentsDataDetailed(
    loggerDeploymentsData: ILoggerDeploymentData[]
  ): ILoggerDeploymentDataDetailed[] {
    const advancedUserRole = getRandomInt(0, 10) > 5;

    return loggerDeploymentsData.map(loggerDeploymentData => {
      return {
        ...loggerDeploymentData,
        distanceFromPipeNode: getRandomInt(100, 1000),
        nbiotStatus: getRandomItem(enumToArray(LoggerNbiotStatus)),
        rssi: getRandomInt(0, 100),
        rsrp: getRandomInt(0, 100),
        eclForRegistration: getRandomInt(0, 2),
        signalPower: getRandomInt(0, 10),
        syncedProjectTimeZone: getRandomDateTime(100).toISOString(),
        usedBandForRegistration: getRandomInt(0, 10),
        relativeHumidity: getRandomInt(0, 10),
        nextContactAt: getRandomDateTime(0).toISOString(),
        batteryReplacementAt: getRandomDateTime(100).toISOString(),
        peakNoiseDecibel: getRandomInt(0, 5),
        peakNoiseDecibelStatus: getRandomInt(0, 10) > 7 ? getRandomItem(enumToArray(CommonStateStatus)) : undefined,
        radioOperationTime: getRandomInt(0, 1000),
        radioOperationTimeStatus: getRandomInt(0, 10) > 7 ? getRandomItem(enumToArray(CommonStateStatus)) : undefined,
        roundTripTime: getRandomInt(0, 1000),
        batteryLifePercent: getRandomInt(0, 100),
        loggerRotated: getRandomBoolean(),
        loggerOrientation:
          getRandomInt(0, 10) > 7
            ? {
                pitch: getRandomInt(0, 10),
                pitchStatus: getRandomItem(enumToArray(PositionStatus)),
                roll: getRandomInt(0, 10),
                rollStatus: getRandomItem(enumToArray(PositionStatus)),
              }
            : undefined,
        manuallyMovedAt: getRandomDateTime(100).toISOString(),
        usedBandForMeasurement: getRandomInt(0, 30),
        servingCellIdUnexpected: getRandomBoolean(),
        powerOffResetCount: getRandomInt(0, 10),
        powerLossEventCount: getRandomInt(0, 10),
        modemFailureCount: getRandomInt(0, 10),
        antennaType: getRandomItem(enumToArray(DeviceAntennaType)),
        repeaterSerialNo: getRandomInt(0, 10) > 7 ? getRandomInt(100000000, 999999999).toString() : undefined,
        alphaSerialNo: getRandomInt(0, 10) > 7 ? getRandomInt(100000000, 999999999).toString() : undefined,
        assetId: getRandomStr(9),
        mobileNetworkProvider: getRandomStr(9),
        updateFirmwareState: getRandomItem(enumToArray(LoggerUpdateFirmwareState)),
        updateFirmwareSentPercent: getRandomInt(0, 100),
        updateOriginFirmwareVersion: 'version' + getRandomStr(9),
        updateTargetFirmwareVersion: 'version' + getRandomStr(9),
        updateFirmwareType: getRandomItem(enumToArray(LoggerUpdateFirmwareType)),
        updateFirmwareLastCommunicationProjectAt: getRandomDateTime(100).toISOString(),
        // advanced user role properties
        sinr: advancedUserRole ? getRandomInt(0, 100) : undefined,
        modemInfo: advancedUserRole ? getRandomStr(getRandomInt(0, 50)) : undefined,
        simImsi: advancedUserRole ? getRandomStr(getRandomInt(0, 50)) : undefined,
        simIccId: advancedUserRole ? getRandomStr(getRandomInt(0, 50)) : undefined,
        temperature: advancedUserRole ? getRandomInt(0, 100) : undefined,
        absoluteHumidity: advancedUserRole ? getRandomInt(0, 100) : undefined,
        prevModemOnTime: advancedUserRole ? getRandomInt(0, 100) : undefined,
        batteryLife: advancedUserRole ? getRandomInt(0, 100) : undefined,
        netCredGatewayAddress: advancedUserRole ? getRandomStr(getRandomInt(0, 50)) : undefined,
        netCredGatewayPort: advancedUserRole ? getRandomInt(0, 100) : undefined,
        netCredUsedBands: advancedUserRole ? getRandomStr(getRandomInt(0, 50)) : undefined,
        netCredMccMnc: advancedUserRole ? getRandomStr(getRandomInt(0, 50)) : undefined,
        netCredApn: advancedUserRole ? getRandomStr(getRandomInt(0, 50)) : undefined,
        netCredApnUser: advancedUserRole ? getRandomStr(getRandomInt(0, 50)) : undefined,
      };
    });
  }

  private generateLoggerDeploymentsData(): ILoggerDeploymentData[] {
    return this.loggerDeploymentsDataPositions.map(position => {
      const loggerType = getRandomItem(enumToArray(LoggerType));
      const identifier = this.id++;
      const comment = getRandomInt(0, 10) > 3 ? 'some comment' : undefined;
      const projectId = this.projectsMockService.resolveProjectId();

      return {
        deploymentId: `0000${identifier}`,
        serialNo: `log0000${identifier}`,
        projectId,
        areaId: this.areasMockService.resolveAreaId(),
        deployTime: getRandomDateTime(100).toISOString(),
        location: 'some location',
        customCoordinates:
          getRandomInt(0, 10) > 7
            ? this.coordinateSystemsMockService.generateCustomCoordinates(projectId, identifier)
            : getRandomItem([undefined, null]),
        coordinates: getRandomInt(1, 5) > 4 ? getRandomPosition(position, 50) : position,
        adjustedCoordinates: position,
        lastReachedAt: getRandomDateTime(100).toISOString(),
        reachability: 2,
        reachabilityStatus: getRandomItem(enumToArray(CommonStateStatus)),
        deviceStatus: getRandomItem(enumToArray(DeviceStatus)),
        batteryVoltage: 3.2,
        batteryVoltageStatus: getRandomItem(enumToArray(CommonStateStatus)),
        minTemp: 10,
        minTempStatus: getRandomItem(enumToArray(CommonStateStatus)),
        maxTemp: 20,
        maxTempStatus: getRandomItem(enumToArray(CommonStateStatus)),
        firmwareVersion: '1.0.0',
        lastServiceAt: getRandomDateTime(100).toISOString(),
        comment: comment
          ? {
              content: comment,
              author: getRandomInt(0, 10) > 7 ? getRandomStr(getRandomInt(5, 50)) : undefined,
              modifiedAt: getRandomInt(0, 10) > 7 ? getRandomDateTime(100).toISOString() : undefined,
            }
          : undefined,
        devicePictureIds: getRandomInt(0, 5) > 4 ? [getRandomStr(9)] : undefined,
        servingCellId: getRandomInt(100000000, 999999999).toString(),
        loggerType,
        eclForMeasurement: getRandomInt(0, 5) > 1 ? getRandomItem(enumToArray(LoggerEcl)) : undefined,
      };
    });
  }
}
